import { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Button, DataTable, InlineStack, Select, Text, TextField } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { LegacyBackendOwner, LegacyBackendPilot } from '../../app/models/Admin';
import { formatLocalDateTime } from './utils/dateTime';
import MigrationDetails from './MigrationDetails';
import PilotFailureDetails from './PilotFailureDetails';

type Props = { sourceShopId: number; token: string; onUnauthorized: () => void };
const owners: LegacyBackendOwner[] = ['PFV1', 'PFV2', 'Paused'];
const canonicalDomain = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/;

export function validPilotState(value: LegacyBackendPilot, sourceShopId: number): boolean {
  return !!value && value.sourceShopId === sourceShopId && (value.owner === 'Unknown' || owners.includes(value.owner))
    && Number.isSafeInteger(value.revision) && value.revision >= 0 && typeof value.enabled === 'boolean'
    && (value.domain === null || typeof value.domain === 'string') && Array.isArray(value.blockers)
    && value.blockers.every(x => typeof x === 'string') && Array.isArray(value.capabilities)
    && value.capabilities.every(x => !!x && typeof x.name === 'string' && typeof x.ready === 'boolean' && typeof x.detail === 'string')
    && (!value.targetShopId || (!!value.replication && Number.isSafeInteger(value.replication.pendingChanges)
      && value.replication.pendingChanges >= 0))
    && !!value.privacy && Number.isSafeInteger(value.privacy.pending) && value.privacy.pending >= 0
    && Number.isSafeInteger(value.privacy.overdue) && value.privacy.overdue >= 0 && value.privacy.overdue <= value.privacy.pending
    && Array.isArray(value.privacy.recentCases) && value.privacy.recentCases.every(x => !!x && typeof x.id === 'string'
      && typeof x.topic === 'string' && typeof x.status === 'string' && typeof x.receivedUtc === 'string' && typeof x.dueUtc === 'string');
}

export default function LegacyBackendPilotPanel({ sourceShopId, token, onUnauthorized }: Props) {
  const [pilot, setPilot] = useState<LegacyBackendPilot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [nextOwner, setNextOwner] = useState('');
  const load = useCallback(async () => {
    setLoading(true); setError(null); setPilot(null); setConfirmation(''); setNextOwner('');
    try {
      const response = await agent.Admin.legacyBackendPilot(token, sourceShopId);
      if (!validPilotState(response, sourceShopId)) throw new Error('Invalid pilot readiness response.');
      setPilot(response);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
      else setError('Backend pilot controls are unavailable. No route was changed. Deploy or check the pilot API, then reload its status.');
    } finally { setLoading(false); }
  }, [sourceShopId, token, onUnauthorized]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!['Queued', 'Running'].includes(pilot?.garagePreparation?.status || '')) return;
    const timer = window.setInterval(() => { void load(); }, 15000);
    return () => window.clearInterval(timer);
  }, [pilot?.garagePreparation?.status, load]);
  const activationReady = !!pilot?.targetShopId && pilot.capabilities.length > 0
    && pilot.capabilities.every(x => x.ready) && pilot.blockers.length === 0;
  const controlsAvailable = !!pilot && (pilot.enabled || pilot.owner === 'PFV2' || pilot.owner === 'Paused');
  const canChange = !!pilot && controlsAvailable && !loading && !busy && !error
    && pilot.owner !== 'Unknown' && !!pilot.domain && canonicalDomain.test(pilot.domain) && confirmation === pilot.domain
    && owners.includes(nextOwner as LegacyBackendOwner) && nextOwner !== pilot.owner
    && (nextOwner !== 'PFV2' || (pilot.enabled && activationReady
      && !['Queued', 'Running'].includes(pilot.garagePreparation?.status || '')));
  const changeOwner = async () => {
    if (!pilot || !canChange) return;
    const explanation = nextOwner === 'PFV2'
      ? 'If PFV1 is serving this store, SyncService will scan all garages, verify coverage, catch up changes, and switch to PFV2 only after the final handoff succeeds. PFV1 continues serving during preparation. Resuming a paused PFV2 pilot does not rescan.'
      : nextOwner === 'Paused'
        ? 'Pilot operations will stop. Requests will not silently fall back to PFV1.'
        : 'Current operations will finish and pending redundant writes will drain before PFV1 resumes. If delivery fails or a write is uncertain, the owner will not change. No bulk copy-back is performed.';
    if (!window.confirm(`${explanation}\n\nStore: ${pilot.domain}\nChange backend owner from ${pilot.owner} to ${nextOwner}?`)) return;
    setBusy(true); setError(null);
    try {
      const response = await agent.Admin.updateLegacyBackendPilot(token, sourceShopId, {
        expectedRevision: pilot.revision, owner: nextOwner as LegacyBackendOwner, confirmation,
      });
      if (!validPilotState(response, sourceShopId)) throw new Error('Invalid pilot update response.');
      setPilot(response); setConfirmation(''); setNextOwner('');
    } catch (e) {
      // Never retry a route change using a stale revision after an ambiguous result.
      setPilot(null);
      if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
      else setError(axios.isAxiosError(e) && typeof e.response?.data?.message === 'string'
        ? `${e.response.data.message} Reload pilot status before trying again.`
        : 'The backend change was not confirmed. Reload pilot status to determine the current owner before trying again.');
    } finally { setBusy(false); }
  };
  const preparation = pilot?.garagePreparation;
  const preparing = ['Queued', 'Running'].includes(preparation?.status || '');
  const failedChecks = pilot?.capabilities.filter(x => !x.ready) || [];
  const blockers = Array.from(new Set([...failedChecks.map(x => x.detail), ...(pilot?.blockers || [])]));
  return <BlockStack gap="400">
    <InlineStack align="space-between" blockAlign="center">
      <Text as="h3" variant="headingMd">Backend pilot</Text>
      <Button onClick={() => void load()} loading={loading} disabled={busy}>Reload pilot status</Button>
    </InlineStack>
    {error ? <Banner tone="critical">{error}</Banner> : null}
    {pilot ? <>
      <div className="migration-summary">
        <div className="migration-summary-item"><BlockStack gap="200">
          <Text as="h4" variant="headingSm">Serving backend owner</Text>
          <InlineStack><Badge tone={pilot.owner === 'PFV2' ? 'success' : pilot.owner === 'PFV1' ? 'info' : 'warning'}>{pilot.owner}</Badge></InlineStack>
          <Text as="p" tone="subdued">{pilot.owner === 'PFV2' ? 'Pilot is serving this store.'
            : pilot.owner === 'PFV1' ? 'PFV1 is serving this store.' : pilot.owner === 'Paused' ? 'Operations paused. No automatic fallback.' : 'Reload to verify ownership.'}</Text>
        </BlockStack></div>
        <div className="migration-summary-item"><BlockStack gap="200">
          <Text as="h4" variant="headingSm">Activation checks</Text>
          <Text as="p" variant="headingMd">{activationReady ? 'All checks passed' : (failedChecks.length || 'Required') + ' checks need attention'}</Text>
          <Text as="p" tone="subdued">{pilot.capabilities.filter(x => x.ready).length} of {pilot.capabilities.length} API checks passed.
            {preparation?.status === 'Failed' ? ' The last activation attempt failed.' : ''}</Text>
        </BlockStack></div>
        {pilot.replication ? <div className="migration-summary-item"><BlockStack gap="200">
          <Text as="h4" variant="headingSm">Live redundant writes to PFV1</Text>
          <Text as="p" variant="headingMd">{pilot.replication.pendingChanges} pending writes</Text>
          <Text as="p" tone="subdued">{pilot.replication.errorCode ? 'Delivery needs attention.'
            : pilot.owner === 'PFV1' && pilot.revision === 0 ? 'Writes begin when PFV2 serves the store.' : pilot.replication.lastState || 'Not started'}</Text>
        </BlockStack></div> : null}
      </div>
      {preparation?.status === 'Failed' ? <Banner tone="critical" title="Last activation attempt failed">
        <BlockStack gap="200">
          <PilotFailureDetails operation={preparation} />
          <Text as="p">{preparation.customersScanned} customers scanned · {preparation.validVehicleEntries} vehicles · {preparation.invalidRecords} records needing review.</Text>
          <Text as="p">Review the failure before requesting another activation. The current serving backend is shown above.</Text>
        </BlockStack>
      </Banner> : null}
      {preparing && preparation ? <Banner tone="info" title="Garage preparation">
        {preparation.status === 'Queued' ? 'Waiting for the worker.' : 'Preparing and verifying saved garages.'}
        {' '}{preparation.customersScanned} customers scanned · {preparation.validVehicleEntries} vehicles.
        {pilot.owner === 'PFV1' ? ' PFV1 continues serving during the scan.' : ' Current backend: ' + pilot.owner + '.'}
        {' '}Status refreshes automatically.
      </Banner> : null}
      {blockers.length ? <Banner tone="warning" title="Resolve before activating PFV2">
        <ul>{blockers.map(reason => <li key={reason}>{reason}</li>)}</ul>
      </Banner> : null}
      {!pilot.capabilities.length ? <Banner tone="warning">No verified capabilities have been reported. PFV2 activation is blocked.</Banner> : null}
      {!pilot.targetShopId ? <Banner tone="info">Prepare this store’s PFV2 target in the target preparation section below.</Banner> : null}
      {!pilot.enabled ? <Banner tone="info">Backend pilot execution is disabled on the server. Existing pilots can still be paused or returned to PFV1 after pending writes are delivered.</Banner> : null}
      {pilot.owner === 'Paused' ? <Banner tone="warning">Pilot operations are paused. Choose a backend to resume.</Banner> : null}
      {pilot.replication?.errorCode ? <Banner tone="warning">A redundant write needs retry. Returning to PFV1 will wait until delivery succeeds.</Banner> : null}
      {pilot.privacy.pending > 0 ? <Banner tone={pilot.privacy.overdue > 0 ? 'critical' : 'warning'} title="Privacy requests need attention">
        {pilot.privacy.pending} pending · {pilot.privacy.overdue} overdue. <a href="/pf-console/privacy-requests">Open privacy requests</a>
      </Banner> : null}
      <div className="migration-action"><BlockStack gap="300">
        <Text as="h4" variant="headingSm">Change serving backend</Text>
        <Text as="p" tone="subdued">PFV2 activation prepares saved garages before switching. The merchant’s storefront experience stays the same.</Text>
        <Select label="Requested backend owner" value={nextOwner} onChange={value => { setNextOwner(value); setConfirmation(''); }}
          disabled={!controlsAvailable || loading || busy} options={[
            { label: 'Choose an action', value: '' }, { label: 'PFV2 — prepare garages and activate', value: 'PFV2' },
            { label: 'Paused — stop pilot operations', value: 'Paused' }, { label: 'PFV1 — drain writes and switch back', value: 'PFV1' },
          ]} />
        <TextField label="Type the exact Shopify domain to confirm" value={confirmation} onChange={setConfirmation} autoComplete="off"
          helpText={pilot.domain || 'A verified Shopify domain is required.'} disabled={!controlsAvailable || loading || busy} />
        <InlineStack><Button variant="primary" tone="critical" onClick={() => void changeOwner()} loading={busy} disabled={!canChange}>Change serving backend</Button></InlineStack>
      </BlockStack></div>
      <BlockStack gap="0">
        <MigrationDetails title={'Readiness details (' + pilot.capabilities.filter(x => x.ready).length + '/' + pilot.capabilities.length + ' passed)'}>
          <Text as="p" tone="subdued">These are the API’s current checks. Activation also checks the worker’s configuration; failed worker checks appear in the activation error above.</Text>
          {pilot.capabilities.map(x => <div className="migration-check" key={x.name}>
            <BlockStack gap="100"><Text as="h4" variant="headingSm">{x.name}</Text><InlineStack><Badge tone={x.ready ? 'success' : 'attention'}>{x.ready ? 'Ready' : 'Blocked'}</Badge></InlineStack></BlockStack>
            <Text as="p" tone="subdued">{x.detail}</Text>
          </div>)}
        </MigrationDetails>
        <MigrationDetails title="Route & activation details">
          <BlockStack gap="300">
            <Text as="p">Backend ownership is separate from the Legacy/Native storefront mode.</Text>
            <DataTable columnContentTypes={['text', 'text']} headings={['Route', 'Value']} rows={[
              ['Merchant', pilot.domain || 'Unverified'], ['PFV1 source shop', '#' + pilot.sourceShopId],
              ['PFV2 target shop', pilot.targetShopId ? '#' + pilot.targetShopId : 'Not prepared'],
              ['Route revision', pilot.revision.toString()],
              ['Last route change', pilot.lastChangedAt ? formatLocalDateTime(pilot.lastChangedAt) + ' · ' + (pilot.lastChangedBy || 'Unknown operator') : 'No route change recorded'],
              ...(preparation ? [
                ['Last activation request', formatLocalDateTime(preparation.requestedAt)],
                ['Operation ID', preparation.id], ['Staging run', preparation.stagingRunId || 'Not started'],
                ['Last worker update', formatLocalDateTime(preparation.heartbeatAt)],
              ] : []),
            ]} />
            <Text as="p">During the pilot, PFV2 keeps PFV1 current through redundant writes. Returning waits for pending writes and current operations to finish. Re-entering PFV2 refreshes its baseline from PFV1.</Text>
            {pilot.replication?.lastCompletedUtc ? <Text as="p">Last delivery: {formatLocalDateTime(pilot.replication.lastCompletedUtc)}.</Text> : null}
          </BlockStack>
        </MigrationDetails>
        <MigrationDetails title={'Privacy requests (' + pilot.privacy.pending + ' pending)'}>
          <BlockStack gap="300">
            <Text as="p">Pending fulfillment: {pilot.privacy.pending}. Overdue: {pilot.privacy.overdue}.</Text>
            <Text as="p" tone="subdued">Recorded requests have been received, not fulfilled. Manage exports and deletion in <a href="/pf-console/privacy-requests">Privacy requests</a>. This view intentionally excludes customer details.</Text>
            {pilot.privacy.recentCases.length ? <DataTable columnContentTypes={['text', 'text', 'text', 'text', 'text']}
              headings={['Case ID', 'Request', 'Status', 'Received', 'Due']}
              rows={pilot.privacy.recentCases.map(x => [x.id, x.topic,
                x.status === 'PendingFulfillment' ? 'Pending fulfillment' : x.status,
                formatLocalDateTime(x.receivedUtc), formatLocalDateTime(x.dueUtc)])} />
              : <Text as="p">No privacy requests recorded.</Text>}
          </BlockStack>
        </MigrationDetails>
      </BlockStack>
    </> : null}
  </BlockStack>;
}
